import { api } from '../../lib/api';
export type PublishChannel = 'YOUTUBE' | 'INSTAGRAM' | 'FACEBOOK';
export const publishingChannels: PublishChannel[] = ['YOUTUBE', 'INSTAGRAM', 'FACEBOOK'];
export interface PublishOptions {
  madeForKids: boolean;
  shareToFeed: boolean;
  facebookMediaType: 'REELS' | 'VIDEO';
}
export interface PublicationRow {
  id: string;
  groupId?: string;
  assetId: string;
  title: string;
  channel: PublishChannel;
  date: string;
  time: string;
  publishAt: string | null;
  version: number;
  options: PublishOptions;
  ready: boolean;
  issue: string | null;
  state: string;
  reason: string | null;
  liveUrl: string | null;
  attemptId: string | null;
  removedAt?: string | null;
  reviewWaiver?: string | null;
}
export interface PublishingSetting {
  id: string;
  paused: boolean;
  defaultTime: string;
  metaCrosspostingConfirmed: boolean;
  version: number;
}
export interface PublishingWeek {
  from: string;
  until: string;
  timezone: string;
  mode: string;
  posts: PublicationRow[];
  candidates: {
    id: string;
    channel: string;
    title: string | null;
    assetId: string;
    asset: { title: string; slug: string };
  }[];
  pauses: string[];
  settings: PublishingSetting[];
}
export interface Selection {
  postId: string;
  version: number;
}
export interface PublicationPreview {
  previewToken: string;
  mode: string;
  rows: {
    postId: string;
    version: number;
    title?: string;
    channel?: string;
    publishAt?: string;
    caption?: string;
    tags?: string[];
    aiLabel?: boolean;
    advertising?: boolean;
    options?: PublishOptions;
    hasCover?: boolean;
    cover?: { supported: boolean; previewUrl: string } | null;
    reviewWaiver?: string | null;
    ready?: boolean;
    reviewComment?: string | null;
    target?: { profile: string; targetId: string };
    error: string | null;
  }[];
}
const prefix = '/admin/social/publishing';
export const publishingApi = {
  week: (from: string) => api.get<PublishingWeek>(`${prefix}/week?from=${encodeURIComponent(from)}`),
  location: (id: string) =>
    api.get<{ date: string | null }>(`${prefix}/posts/${encodeURIComponent(id)}/location`),
  confirmLive: (row: PublicationRow, input: { url: string; reason: string }) =>
    api.post(`${prefix}/posts/${row.id}/confirm-live`, {
      ...input,
      version: row.version,
      attemptId: row.attemptId,
    }),
  removal: (row: PublicationRow, reason: string) =>
    api.post(`${prefix}/posts/${row.id}/removal`, {
      version: row.version,
      attemptId: row.attemptId,
      reason,
      removedOnPlatform: true,
    }),
  connections: () =>
    api.get<{
      mode: string;
      accounts: { channel: PublishChannel; connected: boolean; targetId?: string; error: string | null }[];
    }>(`${prefix}/connections`),
  clone: (input: {
    requestId: string;
    sourcePostId: string;
    channels: PublishChannel[];
    date: string;
    time?: string;
  }) => api.post<{ ids: string[] }>(`${prefix}/posts`, input),
  schedule: (row: PublicationRow, input: { date: string; time: string; options: PublishOptions }) =>
    api.patch(`${prefix}/posts/${row.id}/schedule`, { ...input, expectedVersion: row.version }),
  preview: (items: Selection[]) => api.post<PublicationPreview>(`${prefix}/preview`, { items }),
  activate: (items: Selection[], previewToken: string, batchId: string) =>
    api.post<{ results: { postId: string; id?: string; state?: string; error?: string }[] }>(
      `${prefix}/activate`,
      { items, previewToken, batchId },
    ),
  cancel: (row: PublicationRow) => api.post(`${prefix}/posts/${row.id}/cancel`, { version: row.version }),
  unschedule: (row: PublicationRow) =>
    api.post(`${prefix}/posts/${row.id}/unschedule`, { version: row.version }),
  pause: (date: string, paused: boolean) => api.post(`${prefix}/pause`, { date, paused }),
  setting: (
    setting: PublishingSetting,
    patch: Partial<Pick<PublishingSetting, 'paused' | 'defaultTime' | 'metaCrosspostingConfirmed'>>,
  ) => api.patch(`${prefix}/settings/${setting.id}`, { ...patch, expectedVersion: setting.version }),
  reconcile: (id: string) => api.post(`${prefix}/attempts/${id}/reconcile`),
};
